import { Building2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useT } from "../i18n";

interface CompanyCaptureProps {
  companySuggestion: string | null;
  onConfirmCompany: (companyName: string) => void;
}

export function CompanyCapture({
  companySuggestion,
  onConfirmCompany,
}: CompanyCaptureProps) {
  const t = useT();
  const [companyName, setCompanyName] = useState(companySuggestion ?? "");
  const [error, setError] = useState("");

  useEffect(() => {
    setCompanyName(companySuggestion ?? "");
  }, [companySuggestion]);

  const handleConfirmCompany = () => {
    if (!companyName.trim()) {
      setError(t("companyCapture.errorEmpty"));
      return;
    }

    setError("");
    onConfirmCompany(companyName.trim());
  };

  return (
    <div
      className="min-h-screen p-6"
      style={{
        background: "linear-gradient(to bottom right, #02324A, #00AFFD)",
      }}
    >
      <div className="mx-auto max-w-2xl py-8">
        <div className="rounded-2xl bg-white p-8 shadow-xl">
          <div className="mb-6 text-center">
            <div
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
              style={{ backgroundColor: "#00AFFD" }}
            >
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <h2 className="mb-3 font-[Roboto_Slab] text-2xl text-[#02324a]">
              {t("companyCapture.title")}
            </h2>
            <p className="font-[Roboto] text-gray-700">
              {t("companyCapture.subtitle")}
            </p>
          </div>

          <div className="mb-6 rounded-xl border border-blue-100 bg-blue-50 p-5">
            <p className="mb-3 font-[Roboto] text-gray-700">
              {companySuggestion
                ? t("companyCapture.withSuggestion")
                : t("companyCapture.noSuggestion")}
            </p>
            <input
              type="text"
              className={`w-full rounded-lg border-2 px-4 py-3 font-[Roboto] transition-colors focus:outline-none ${
                error
                  ? "border-red-500 focus:border-red-500"
                  : "border-gray-300 focus:border-[#FF00D2]"
              }`}
              placeholder={t("companyCapture.placeholder")}
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleConfirmCompany();
                }
              }}
            />
            {error && (
              <p className="mt-2 text-sm font-[Roboto] text-red-500">{error}</p>
            )}
          </div>

          <button
            type="button"
            onClick={handleConfirmCompany}
            className="w-full rounded-lg px-6 py-4 font-[Roboto] text-lg font-medium text-white transition-colors"
            style={{ backgroundColor: "#FF00D2" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#D900B0")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#FF00D2")
            }
          >
            {t("companyCapture.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
